import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { pagesApi } from '@/lib/supabase';
import { useLocalizedContent } from '@/hooks/useLocalizedContent';
import ScrollHeading from '@/components/ScrollHeading';

const CVContact = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { getField, getContent } = useLocalizedContent();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await pagesApi.getPublishedBySlug('cv-contact');
        setPageData(data);
      } catch (err) {
        console.error('Error fetching cv page:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const sections = pageData?.sections || [];
  const cvSections = sections.filter(s => s.section_type === 'cv_section');
  const contactSection = sections.find(s => s.section_type === 'contact');

  return (
    <div className="min-h-screen bg-[#FDFBF7] px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div>
        <ScrollHeading 
          className="heading-display mb-16"
          dataTestId="cv-heading"
        >
          {(pageData && getField(pageData, 'title')) || t('nav.cv')}
        </ScrollHeading>

        <div className="max-w-3xl space-y-12">
          {cvSections.map((section, index) => {
            const sectionContent = getContent(section);
            return (
              <motion.section
                key={section.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 + (index * 0.1), ease: [0.16, 1, 0.3, 1] }}
                className="border-b border-[#E5E0D6] pb-8"
              >
                <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[#8C8C8C] mb-6">
                  {sectionContent?.label || section.title}
                </h3>
                <div className="space-y-4">
                  {sectionContent?.items?.map((item, itemIndex) => (
                    <div key={itemIndex} className="grid grid-cols-1 md:grid-cols-12 gap-4">
                      <div className="md:col-span-3">
                        <span className="font-lexend font-light text-base text-[#8C8C8C]">{item.year}</span>
                      </div>
                      <div className="md:col-span-9">
                        <p className="font-lexend font-light text-lg leading-relaxed tracking-wide text-[#1C1C1C]">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.section>
            );
          })}

          {/* Contact */}
          {contactSection && (() => {
            const contactContent = getContent(contactSection);
            return (
              <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="pt-8"
              >
                <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[#8C8C8C] mb-6">
                  {contactContent?.label || t('contact.title')}
                </h3>
                <div>
                  <a 
                    href={`mailto:${contactContent?.email || contactSection.content?.email || 'contact@example.com'}`}
                    className="font-lexend font-normal text-lg underline decoration-1 underline-offset-4 hover:decoration-2 transition-all text-[#1C1C1C]"
                    data-testid="contact-email"
                  >
                    {contactContent?.email || contactSection.content?.email || 'contact@example.com'}
                  </a>
                </div>
                {contactContent?.description && (
                  <p className="font-lexend font-light text-base leading-relaxed tracking-wide text-[#8C8C8C] mt-6">
                    {contactContent.description}
                  </p>
                )}
              </motion.section>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default CVContact;
